import {Col, Divider, Form, Input, Row, Select} from "antd";
import {useGetCategoriesQuery, useGetCitiesQuery} from "../../../store/api/CategoryApi";
import React, {useMemo} from "react";
import {PartnerCreateDTO} from "../../../types/partner/PartnerCreateDTO";
import {AddressDTO} from "../../../types/partner/AddressDTO";
import {PartnerDTO} from "../../../types/partner/PartnerDTO";
import {StreetDTO} from "../../../types/partner/StreetDTO";
import {useGetOrderTypesQuery} from "../../../store/api/OrderApi";
import {isDeliveryByClient, isPickup, ServiceType, serviceTypeLabel} from "../../../types/order/OrderTypeDTO";
import {BranchDTO} from "../../../types/branch/BranchDTO";

const {TextArea} = Input

interface PartnerCreateFormProps {
    partner: PartnerDTO | undefined
    onFinish: (data: PartnerCreateDTO) => void
}

const PartnerCreateForm: React.FC<PartnerCreateFormProps> = ({partner, onFinish}) => {
    const [form] = Form.useForm()
    const {data: categories} = useGetCategoriesQuery({
        page: 1,
        size: 20
    })
    const {data: cities} = useGetCitiesQuery({
        page: 1,
        size: 30
    })
    const {data: orderTypes} = useGetOrderTypesQuery()

    const serviceType: ServiceType | undefined = Form.useWatch('service_type', form)
    const cityId: string | undefined = Form.useWatch('city_id', form)
    const pickupSelected = isPickup(serviceType)
    const deliveryByClientSelected = isDeliveryByClient(serviceType)

    const restaurants: BranchDTO[] = useMemo(() => {
        if (deliveryByClientSelected) {
            return cities?.data?.find(city => city.id === cityId)?.organizations ?? []
        }
        const list: BranchDTO[] = []
        cities?.data?.forEach(city => {
            city.organizations?.forEach(b => list.push(b))
        })
        return list
    }, [cities, deliveryByClientSelected, cityId])

    const branchFilterOption = (input: string, option?: { label: string; value: string }) => {
        if (!option) return false
        return option.label.toLowerCase().includes(input.toLowerCase())
    }

    const branchOptions = restaurants.map(b => ({
        value: b.id,
        label: b.address ? `${b.name} — ${b.address}` : b.name
    }))
    
    const findBranchById = (id: string | undefined): BranchDTO | undefined => {
        if (!id) return undefined
        for (const city of cities?.data ?? []) {
            const found = city.organizations?.find(o => o.id === id)
            if (found) return found
        }
        return undefined
    }

    const onFinishHandler = async (values: any) => {
        const serviceTypeValue: ServiceType = values.service_type
        const isPickupOrder = isPickup(serviceTypeValue)
        const isDeliveryByClientOrder = isDeliveryByClient(serviceTypeValue)

        const serviceTypeChanged = serviceTypeValue !== partner?.service_type
        const unchanged = (value: unknown, previous: unknown): boolean =>
            !serviceTypeChanged && value === previous

        const resolveBranch = (): BranchDTO | null => {
            if (unchanged(values.branch_id, partner?.address.branch?.id)) return null
            const selected = findBranchById(values.branch_id)
            return selected ? {id: selected.id, name: selected.name} : null
        }

        let address: AddressDTO

        if (isDeliveryByClientOrder) {
            address = {
                street: {name: null, city: null},
                branch: resolveBranch(),
                city_id: unchanged(values.city_id, partner?.address.city_id) ? null : values.city_id,
                house: null,
                latitude: null,
                longitude: null,
                comment: null
            }
        } else if (isPickupOrder) {
            address = {
                street: {name: null, city: null},
                branch: resolveBranch(),
                city_id: null,
                house: null,
                latitude: null,
                longitude: null,
                comment: null
            }
        } else {
            const selectedCity = cities?.data?.find(city => city.id === values.streetCity)
            const street: StreetDTO = {
                name: unchanged(values.streetName, partner?.address.street.name) ? null : values.streetName.trim(),
                city: unchanged(selectedCity?.name, partner?.address.street.city) ? "" : selectedCity?.name
            }
            const latitude = values.point.substring(0, values.point.indexOf(','))
            const longitude = values.point.substring(values.point.indexOf(' ') + 1)
            address = {
                street,
                branch: null,
                city_id: unchanged(values.city_id, partner?.address.city_id) ? null : values.city_id,
                house: unchanged(values.house, partner?.address.house) ? null : values.house.trim(),
                latitude: unchanged(latitude, partner?.address.latitude) ? null : latitude,
                longitude: unchanged(longitude, partner?.address.longitude) ? null : longitude,
                comment: unchanged(values.comment, partner?.address.comment) ? null : (values.comment ? values.comment.trim() : null)
            }
        }

        const dto: PartnerCreateDTO = {
            name: unchanged(values.name, partner?.name) ? null : values.name.trim(),
            coupon_name: unchanged(values.couponName, partner?.coupon_name) ? null : values.couponName.trim(),
            coupon_percent: unchanged(values.couponPercent, partner?.coupon_percent) ? null : values.couponPercent,
            category_id: unchanged(values.categoryId, partner?.category.id) ? null : values.categoryId,
            service_type: serviceTypeChanged ? serviceTypeValue : null,
            address
        }
        onFinish(dto)
    }

    return (
        <Form form={form} onFinish={onFinishHandler} id={'partner-create-form'} layout="vertical"
              initialValues={{
                  name: partner?.name,
                  categoryId: partner?.category.id,
                  couponName: partner?.coupon_name,
                  couponPercent: partner?.coupon_percent,
                  service_type: partner?.service_type,
                  branch_id: partner?.address.branch?.id,
                  city_id: partner?.address.city_id,
                  streetName: partner?.address.street.name,
                  streetCity: partner?.address.street.city,
                  house: partner?.address.house,
                  point: partner ? `${partner?.address.latitude}, ${partner?.address.longitude}` : undefined,
                  comment: partner?.address.comment
              }}>
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item
                        name="name"
                        label="Название"
                        rules={[{required: true, whitespace: true, message: "Введите название"}]}
                    >
                        <Input placeholder={'Введите название'}/>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="categoryId"
                        label="Категория"
                        rules={[{required: true, message: "Выберите категорию"}]}
                    >
                        <Select
                            placeholder={'Выберите категорию'}
                            options={categories?.data?.content.map(item => ({
                                value: item.id,
                                label: item.name
                            }))}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item
                        name="couponName"
                        label="Купон"
                        rules={[{required: true, whitespace: true, message: "Введите название купона"}]}
                    >
                        <Input placeholder={'Введите название'}/>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="couponPercent"
                        label="Процентная ставка"
                        rules={[{
                            required: true,
                            pattern: /^-?[0-9][0-9]?$|^100$/,
                            message: "Введите процент купона (мин.: -100, макс.: 100)"
                        }]}
                    >
                        <Input placeholder={'Введите процент'}/>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item
                        name="service_type"
                        label="Тип заказа"
                        rules={[{required: true, message: "Выберите тип заказа"}]}
                    >
                        <Select
                            placeholder={'Выберите тип заказа'}
                            options={orderTypes?.data?.map(item => ({
                                value: item.id,
                                label: serviceTypeLabel(item.id)
                            }))}
                            onChange={() => {
                                form.setFieldsValue({
                                    branch_id: undefined,
                                    streetName: undefined,
                                    city_id: undefined,
                                    streetCity: undefined,
                                    house: undefined,
                                    point: undefined,
                                    comment: undefined
                                })
                            }}
                        />
                    </Form.Item>
                </Col>
            </Row>

            {deliveryByClientSelected && (
                <>
                    <Divider>Ресторан</Divider>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="city_id"
                                label="Город"
                                rules={[{required: true, message: "Выберите город"}]}
                            >
                                <Select
                                    placeholder={'Выберите город'}
                                    options={cities?.data?.map(item => ({
                                        value: item.id,
                                        label: item.name
                                    }))}
                                    onChange={() => {
                                        form.setFieldsValue({branch_id: undefined})
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="branch_id"
                                label="Ресторан"
                                rules={[{required: true, message: "Выберите ресторан"}]}
                            >
                                <Select
                                    showSearch
                                    disabled={!cityId}
                                    placeholder={cityId ? 'Выберите ресторан' : 'Сначала выберите город'}
                                    options={branchOptions}
                                    filterOption={branchFilterOption}
                                    notFoundContent={'Ресторан не найден'}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            )}

            {pickupSelected && !deliveryByClientSelected && (
                <>
                    <Divider>Ресторан</Divider>
                    <Row>
                        <Col span={24}>
                            <Form.Item
                                name="branch_id"
                                label="Ресторан"
                                rules={[{required: true, message: "Выберите ресторан"}]}
                            >
                                <Select
                                    showSearch
                                    placeholder={'Выберите ресторан'}
                                    options={branchOptions}
                                    filterOption={branchFilterOption}
                                    notFoundContent={'Ресторан не найден'}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            )}

            {!pickupSelected && serviceType !== undefined && (
                <>
                    <Divider>Адрес</Divider>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="streetName"
                                label="Название улицы"
                                rules={[{required: true, whitespace: true, message: "Введите название улицы"}]}
                            >
                                <Input placeholder={'Введите название'}/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="city_id"
                                label="Город"
                                rules={[{required: true, message: "Выберите город"}]}
                            >
                                <Select
                                    placeholder={'Выберите город'}
                                    options={cities?.data?.map(item => ({
                                        value: item.id,
                                        label: item.name
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="point"
                                label="Локация"
                                rules={[{
                                    required: true,
                                    pattern: /^(-?\d+(\.\d+)?), \s*(-?\d+(\.\d+)?)$/,
                                    message: "Введите локацию по шаблону (Пр.: 41.345570, 69.284599)"
                                }]}
                            >
                                <Input placeholder={'41.345570, 69.284599'}/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="house"
                                label="Дом"
                                rules={[{
                                    required: true,
                                    message: "Введите номер дома"
                                }]}
                            >
                                <Input placeholder={'Введите номер дома'}/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Form.Item
                                name="comment"
                                label="Комментария к адресу"
                            >
                                <TextArea placeholder="Введите комментарию" rows={4}/>
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            )}
        </Form>
    )
}

export default PartnerCreateForm
